<script lang="ts" setup>
import { isValidFullDomain } from '@thecompaniesapi/url-helpers'
import type { ApiAttributes, ApiResponse } from '#components'

const { getCompanyAttributes } = useApiAttributes()
const { segmentation } = useSegmentation()
const { t } = useI18n()
const { toggleModal } = useOverlays()
const companiesStore = useCompaniesStore()
const localePath = useLocalePath()
const router = useRouter()
const userStore = useUserStore()

const company = ref<CompanyV2>()
const apiAttributesRef = ref<InstanceType<typeof ApiAttributes>>()
const companyContext = ref<CompanyContext>()
const loadingContext = ref(false)
const apiResponseRef = ref<InstanceType<typeof ApiResponse>>()
const companyProfileRef = ref<HTMLDivElement>()
const companyDomain = ref(segmentation.freeQueries.domain)
const currentTab = ref('response')
const loading = ref(false)

const tabs: UTabItem[] = [
  {
    icon: getIcon('code'),
    label: t('globals.response'),
    value: 'response',
  },
  {
    icon: getIcon('enrichmentFeature'),
    label: t('globals.attributes'),
    value: 'attributes',
  },
  {
    icon: getIcon('documents'),
    label: t('globals.context'),
    value: 'context',
  },
]

const companyAttributes = computed((): ApiAttribute[] => {
  return getCompanyAttributes({ version: 'v2' })
})

watch(
  companyDomain,
  async () => {
    if (!userStore.user) {
      toggleModal({
        data: { currentView: 'login' },
        opened: true,
        title: 'companyProfile',
        type: 'auth',
      })
    }
    else {
      if (isValidFullDomain(companyDomain.value)) {
        await fetchCompanyProfile()
      }
    }
  },
)

async function fetchCompanyProfile() {
  loading.value = true

  try {
    company.value = await companiesStore.fetchCompany({ domain: companyDomain.value })

    resetScrollPosition()
  }
  catch (error) {
    const axiosError = error as AxiosError
    const axiosErrorData = axiosError.response?.data as { message?: string }

    if (axiosErrorData?.message === 'noCreditsRemaining') {
      router.push(localePath('settings-subscription'))
    }
    else {
      console.error(error)
    }
  }

  loading.value = false

  // Return something for asyncData
  return company.value
}

async function fetchCompanyContext() {
  loadingContext.value = true

  try {
    const response = await companiesStore.fetchCompanyContext({ domain: companyDomain.value })

    companyContext.value = {
      domain: companyDomain.value,
      ideated: response.context?.ideated || {},
      sources: response.context?.sources || [],
    }
  }
  catch (error) {
    console.error(error)
  }

  loadingContext.value = false
}

function resetScrollPosition() {
  if (import.meta.client) {
    companyProfileRef.value?.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: 0,
    })

    apiAttributesRef.value?.resetScrollPosition()
    apiResponseRef.value?.resetScrollPosition()
  }
}

async function onTabChange(tabIndex: number) {
  currentTab.value = tabs[tabIndex].value

  if (currentTab.value === 'context') {
    await fetchCompanyContext()
  }
}

await fetchCompanyProfile()
</script>

<template>
  <AnimatedBorder
    class="relative"
    :hoverable="false"
    :opacity="0.2"
    :spread="24"
  >
    <div class="bg-texture-deep h-100 relative flex w-full flex-col rounded-lg shadow-sm ring-gray-200 lg:ring-1 dark:ring-gray-800">
      <UTabs
        class="flex-initial"
        :items="tabs"
        :ui="{
          container: '!rounded-none',
          base: '!rounded-none',
          wrapper: '!rounded-none !space-y-0',
          list: {
            height: 'h-12',
            base: '!rounded-none !bg-transparent',
            tab: {
              height: 'h-10',
              icon: 'size-5',
              padding: 'px-4',
              size: 'text-base',
              font: 'hover:text-gray-900 dark:hover:text-gray-100',
            },
          },
        }"
        @change="onTabChange"
      >
        <template #default="{ item }">
          <span class="xs:inline hidden truncate">
            {{ item.label }}
          </span>
        </template>
      </UTabs>

      <div class="size-full overflow-y-auto p-1.5">
        <ApiResponse
          v-if="currentTab === 'response'"
          ref="apiResponseRef"
          class="size-full !p-0"
          :has-header="false"
          :level-to-open="1"
          :response="company"
        />

        <ApiAttributes
          v-else-if="currentTab === 'attributes'"
          ref="apiAttributesRef"
          :attributes="companyAttributes"
          class="size-full !p-0"
        />

        <ApiResponse
          v-else-if="currentTab === 'context'"
          class="size-full !p-0"
          :has-header="false"
          :level-to-open="1"
          :loading="loadingContext"
          :response="companyContext"
        />
      </div>

      <div class="flex max-w-full items-center gap-2 p-1.5">
        <CompanyAutocomplete
          v-model="companyDomain"
          class="flex-1"
        />

        <UButton
          :label="t('useIt')"
          size="sm"
          :to="localePath({
            name: 'api-slug',
            params: { slug: 'enrich-company-from-domain' },
          })"
          :trailing-icon="getIcon('arrowRight')"
          variant="subtle"
        />
      </div>
    </div>
  </AnimatedBorder>
</template>

<i18n lang="json">
{
  "en": {
    "description": {
      "part1": "We’ve got the data you’re after. If anything’s missing, ping us anytime -",
      "part2": "we can add data points!"
    },
    "title": "Enter a company domain to discover its full profile",
    "useIt": "Use it in your product"
  },
  "es": {
    "description": {
      "part1": "Tenemos los datos que estás buscando. Si falta algo, ponte en contacto con nosotros en cualquier momento,",
      "part2": "podemos añadir puntos de datos!"
    },
    "title": "Introduce un dominio de empresa para descubrir su perfil completo",
    "useIt": "Usalo en tu producto"
  },
  "fr": {
    "description": {
      "part1": "Nous avons les données que vous recherchez. Si quelque chose manque, contactez-nous à tout moment,",
      "part2": "nous pouvons ajouter des points de données !"
    },
    "title": "Entrez un domaine d'entreprise pour découvrir son profil complet",
    "useIt": "Utilisez-le dans votre produit"
  }
}
</i18n>
